import type Luminary from '@lumen5/luminary';
import type { SceneCategory } from '@lumen5/luminary';

import { ListSceneDesignTool } from './ListSceneDesignTool';

export type SceneDesignValidation =
    | { error: string; sceneCategory?: undefined }
    | { error?: undefined; sceneCategory: SceneCategory };

/**
 * Validates a scene_design_id against the loaded template — id format,
 * template prefix, and that the design actually exists in the template's
 * catalog — returning a corrective error message the agent can act on, or the
 * design's category on success so callers can apply their own category rules.
 * Shared by the scene/overlay creation tools so a hallucinated or stale design
 * id fails loudly at the call instead of deep inside Luminary.
 */
export function validateSceneDesignId(
    luminary: Luminary,
    sceneDesignId: string,
): SceneDesignValidation {
    const parts = sceneDesignId.split(':');
    if (parts.length !== 2) {
        return {
            error: `Error: Invalid scene_design_id format "${sceneDesignId}". Expected format is "template:scene_design" (e.g., "nimbus:text_1").`,
        };
    }

    const [templatePrefix] = parts;
    const currentTemplateId = luminary.template.id;
    if (templatePrefix !== currentTemplateId) {
        return {
            error: `Error: Scene design "${sceneDesignId}" is not compatible with the current template "${currentTemplateId}". Please use a scene design from the "${currentTemplateId}" template (e.g., "${currentTemplateId}:text_1").`,
        };
    }

    try {
        return { sceneCategory: luminary.getSceneDesignInfo(sceneDesignId).sceneCategory };
    } catch {
        return {
            error: `Error: Scene design "${sceneDesignId}" was not found in template "${currentTemplateId}". Use ${ListSceneDesignTool.toolName} to see the available designs.`,
        };
    }
}
